import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for existing session
    const initAuth = async () => {
      try {
        const session = await authService.getSession();
        if (session?.user) {
          setUser(session.user);
          const profile = await authService.getCustomerProfile();
          setCustomer(profile);
        }
      } catch (err) {
        console.error('Auth init error:', err.message);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes
    const { data: { subscription } } = authService.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          setUser(session.user);
          // Don't fetch profile here - let signIn handle it to avoid race condition
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setCustomer(null);
        } else if (event === 'TOKEN_REFRESHED' && session?.user) {
          setUser(session.user);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, metadata) => {
    setError(null);
    try {
      const data = await authService.signUp(email, password, metadata);

      // If signup returned a session (email confirmation OFF), set user state
      // so the app recognises we're authenticated without needing a separate signIn.
      if (data.session && data.user) {
        setUser(data.user);
        try {
          const profile = await authService.getCustomerProfile();
          setCustomer(profile);
        } catch (profileErr) {
          console.error('Error getting profile after signup:', profileErr.message);
        }
      }

      return data;
    } catch (err) {
      console.error('Sign up failed:', err.message);
      setError(err.message);
      throw err;
    }
  };

  const signIn = async (email, password) => {
    setError(null);
    try {
      const data = await authService.signIn(email, password);
      setUser(data.user);

      // Try to get customer profile, create if doesn't exist
      let profile = null;
      try {
        profile = await authService.getCustomerProfile();
      } catch (profileErr) {
        console.error('Error getting profile:', profileErr.message);
      }

      if (!profile && data.user) {
        // Profile doesn't exist, create it from user metadata
        const metadata = data.user.user_metadata || {};
        await authService.createCustomerProfile(data.user.id, data.user.email, metadata);
        try {
          profile = await authService.getCustomerProfile();
        } catch (fetchErr) {
          console.error('Error fetching new profile:', fetchErr.message);
        }
      }
      setCustomer(profile);
      return data;
    } catch (err) {
      console.error('Sign in failed:', err.message);
      setError(err.message);
      throw err;
    }
  };

  const signOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
      setCustomer(null);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const resetPassword = async (email) => {
    setError(null);
    try {
      await authService.resetPassword(email);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updatePassword = async (newPassword) => {
    setError(null);
    try {
      await authService.updatePassword(newPassword);
    } catch (err) {
      console.error('Update password failed:', err.message);
      setError(err.message);
      throw err;
    }
  };

  const refreshCustomer = async () => {
    try {
      const profile = await authService.getCustomerProfile();
      setCustomer(profile);
    } catch (err) {
      console.error('Error refreshing customer:', err.message);
    }
  };

  const value = {
    user,
    customer,
    loading,
    error,
    isAuthenticated: !!user,
    isEmailVerified: customer?.email_verified === true,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    updateProfile: authService.updateCustomerProfile,
    refreshCustomer
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
